import { GuestFormResponse } from './api-types';

/**
 * Module-scoped and in-memory only — cleared by a full page reload, never written to storage. Same
 * security profile as the session token it holds.
 *
 * Its job is narrow: let a provider REMOUNT within one page load reuse the session already minted
 * for this link token instead of redeeming again. A stray remount (a service-worker takeover, a
 * browser extension forcing a re-render) would otherwise mint a second session, which rotates the
 * token server-side and 401s the autosave still using the first one. Keyed by link token so a
 * different invitation can never pick up a stale session.
 */
let liveSession: { linkToken: string; response: GuestFormResponse } | null =
  null;

export const getCachedSession = (
  linkToken: string
): GuestFormResponse | null =>
  liveSession && liveSession.linkToken === linkToken
    ? liveSession.response
    : null;

export const setCachedSession = (
  linkToken: string,
  response: GuestFormResponse
): void => {
  liveSession = { linkToken, response };
};

/**
 * After a recovery re-redeem, refresh only the session token and keep the original seed data, so a
 * later remount restores THIS session rather than minting yet another one. If the cache holds a
 * different invitation (or none), store the fresh response wholesale — one invitation's answers
 * must never be paired with another's session.
 */
export const cacheRecoveredSession = (
  linkToken: string,
  response: GuestFormResponse
): void => {
  liveSession =
    liveSession && liveSession.linkToken === linkToken
      ? {
          linkToken,
          response: {
            ...liveSession.response,
            sessionToken: response.sessionToken
          }
        }
      : { linkToken, response };
};

/** Drop the cached session: the invitation is terminal or dead, or a test is resetting. */
export const clearCachedSession = (): void => {
  liveSession = null;
};
